import { useMemo } from 'react';
import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { ViewPreferences } from 'types/user';

export type User = {
  id?: number;
  email: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  company_id?: string;
  company_billing_cycle?: string;
  company_website?: string;
  profile_id?: number;
  is_email_verified: boolean;
  view_preferences?: ViewPreferences;
};

// Atoms
const userAtom = atomWithStorage<User | null>('user', null);
const usersAtom = atom<any[]>([]);
const selectedUserAtom = atom<any | null>(null);
const refetchingUserAtom = atom<boolean>(false);
userAtom.debugLabel = 'user';

const tokenAtom = atomWithStorage<string>('access_token', '');
const refreshTokenAtom = atomWithStorage<string>('refresh_token', '');
tokenAtom.debugLabel = 'access_token';
refreshTokenAtom.debugLabel = 'refresh_token';

const useUserAtom = () => useAtom(userAtom);
export const useUsersAtom = () => useAtom(usersAtom);
export const useSelectedUserAtom = () => useAtom(selectedUserAtom);
export const useRefetchingUserAtom = () => useAtom(refetchingUserAtom);

export const useUser = () => {
  const [user, setUser] = useUserAtom();
  const [token, setToken] = useAtom(tokenAtom);
  const [refreshToken, setRefreshToken] = useAtom(refreshTokenAtom);

  const userInitials = useMemo(
    () =>
      [
        user?.first_name?.charAt(0)?.toUpperCase() ?? '',
        user?.last_name?.charAt(0)?.toUpperCase() ?? '',
      ]
        .join(' ')
        .trim(),
    [user?.first_name, user?.last_name],
  );

  const isUserEmailVerified = useMemo(
    () => !!user?.is_email_verified,
    [user?.is_email_verified],
  );

  // Check if user is authenticated
  const isAuthenticated = useMemo(() => {
    return !!token && !!user;
  }, [token, user]);

  return {
    user,
    userInitials,
    useUser,
    setUser,
    token,
    refreshToken,
    setRefreshToken,
    setToken,
    isUserEmailVerified,
    isAuthenticated,
  };
};
